import { Button, Heading, Stack, Tag, Text, useDisclosure } from '@chakra-ui/react';
import { ThemeTypings } from '@chakra-ui/styled-system';
import {
  DataTable,
  DataTableAddFilter,
  DataTableFilter,
  DataTableFilters,
  DataTableSearch,
} from '@pelicargo/datatable';
import { createPrismaQuery, createPrismaSort, useCrud } from '@pelicargo/datatable';
import { Show } from '@pelicargo/ui';
import { getPrettyBacklogStatus } from '@pelicargo/utils';
import { Backlog, BacklogStatus } from '@prisma/client';
import { createColumnHelper } from '@tanstack/react-table';
import { areIntervalsOverlapping, format, fromUnixTime, interval, isWithinInterval } from 'date-fns';
import { maxTime } from 'date-fns/constants';
import { useCallback, useMemo, useState } from 'react';

import { BacklogDrawer } from '../components/Drawers/BacklogDrawer';
import { trpc } from '../config/trpc';

export const backlogFilters = [
  {
    name: 'airport_id',
    label: 'Airport ID',
    inputType: 'string',
    type: 'string',
    operator: 'contains',
    mode: 'insensitive',
    value: '',
  },
  {
    name: 'provider_id',
    label: 'Airline ID',
    inputType: 'string',
    type: 'string',
    operator: 'contains',
    mode: 'insensitive',
    value: '',
  },
  {
    name: 'starts_at',
    label: 'Starts After',
    inputType: 'date',
    type: 'date',
    operator: 'gt',
    value: new Date(),
  },
  {
    name: 'expires_at',
    label: 'Ends Before',
    inputType: 'date',
    type: 'date',
    operator: 'lt',
    value: new Date(),
  },
] satisfies DataTableFilter[];

type TimeRelation = 'Future' | 'Active' | 'Resolved';

type TableColumns = {
  timeRelation?: TimeRelation;
  status: BacklogStatus;
  starts_at: Date;
  expires_at: Date;
  provider_id: string;
  airport_id: string;
  note: string;
};

export const BacklogsScreen = () => {
  const backlogDrawer = useDisclosure();

  const { query, handleQueryChange, filters, setFilters, sorting, setSorting, pagination, setPagination, take, skip } =
    useCrud();

  const [selectedBacklog, setSelectedBacklog] = useState<Backlog>();

  const { data, refetch } = trpc.getAllBacklogs.useQuery({
    filters: createPrismaQuery(filters),
    orderBy: createPrismaSort(sorting),
    search: query,
    skip,
    take,
  });

  const handleAddFilter = (filter: DataTableFilter) => () => {
    setFilters((prevFilters) => [...prevFilters, filter]);
  };

  const handleClear = () => {
    setFilters([]);
  };

  const handleClose = () => {
    refetch();
    setSelectedBacklog(undefined);
    backlogDrawer.onClose();
  };

  const handleRowClick = useCallback(
    (row: Backlog) => {
      setSelectedBacklog(row);
      backlogDrawer.onOpen();
    },
    [backlogDrawer],
  );

  const columnHelper = createColumnHelper<TableColumns>();

  const columns = useMemo(
    () => [
      columnHelper.accessor('timeRelation', {
        header: 'Status',
        cell: (cell) => {
          const original = cell.row.original;
          const activeInterval = interval(original.starts_at, original.expires_at ?? maxTime);
          const within = isWithinInterval(new Date(), activeInterval);
          const inPast = areIntervalsOverlapping(interval(fromUnixTime(0), Date.now()), activeInterval);

          let status: TimeRelation = 'Future';
          let color: ThemeTypings['colorSchemes'] = 'purple';
          if (within) {
            status = 'Active';
            color = 'green';
          } else if (inPast) {
            status = 'Resolved';
            color = 'gray';
          }

          return <Tag colorScheme={color}>{status}</Tag>;
        },
      }),
      columnHelper.accessor('status', {
        header: 'Type',
        cell: (cell) => {
          let color = '';
          if (cell.getValue() === 'AtRisk') {
            color = 'orange';
          } else if (cell.getValue() === 'Backlogged') {
            color = 'red';
          }
          return <Tag colorScheme={color}>{getPrettyBacklogStatus(cell.getValue())}</Tag>;
        },
      }),
      columnHelper.accessor('provider_id', {
        header: 'Airline',
        cell: (cell) => <Text>{cell.renderValue()}</Text>,
      }),
      columnHelper.accessor('airport_id', {
        header: 'Airport',
        cell: (cell) => <Text>{cell.renderValue()}</Text>,
      }),
      columnHelper.accessor('starts_at', {
        header: 'Start',
        cell: (cell) => {
          return format(cell.getValue(), 'MMM d yyyy');
        },
      }),
      columnHelper.accessor('expires_at', {
        header: 'End',
        cell: (cell) => {
          return cell.getValue() === null ? '-' : format(cell.getValue(), 'MMM d yyyy');
        },
      }),
      columnHelper.accessor('note', {
        header: 'Note',
        cell: (cell) => {
          return <Text>{cell.getValue()}</Text>;
        },
      }),
    ],
    [columnHelper],
  );

  return (
    <>
      <Stack spacing="0" h="full">
        <Stack p="4" spacing="4">
          <Stack direction="row" justify="space-between" zIndex="99" w="full">
            <Stack direction="row" align="center" w="full">
              <Heading variant="h3">Backlogs</Heading>
              <DataTableSearch value={query} onChange={handleQueryChange} />
              <DataTableAddFilter
                activeFilters={filters?.map((filter) => filter.name)}
                options={backlogFilters}
                handleAddFilter={handleAddFilter}
              />
            </Stack>
            <Button
              colorScheme="brand"
              onClick={() => {
                setSelectedBacklog(undefined);
                backlogDrawer.onOpen();
              }}
            >
              Create Backlog
            </Button>
          </Stack>
          <Show if={filters.length > 0}>
            <DataTableFilters filters={filters} setFilters={setFilters} onClear={handleClear} />
          </Show>
        </Stack>
        <DataTable
          columns={columns}
          data={data?.results ?? []}
          dataCount={data?.count ?? 0}
          pagination={pagination}
          setPagination={setPagination}
          sorting={sorting}
          setSorting={setSorting}
          onRowClick={handleRowClick}
        />
      </Stack>
      <BacklogDrawer disclosure={{ ...backlogDrawer, onClose: handleClose }} selected={selectedBacklog} />
    </>
  );
};
